import { useEffect, useState } from 'react';

import { Spinner } from '@chakra-ui/react';
import { t } from 'i18next';
import { chunk, isEmpty, isNull } from 'lodash';
import EasyVirtualized from 'react-easy-virtualized';
import 'react-horizontal-scrolling-menu/dist/styles.css';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { ArrowRight } from '@peacecover/icon';

import baseUrl from '@pocapan/shared/utils/baseUrl';
import getToken from '@pocapan/shared/utils/getToken';
import { queryGet } from '@pocapan/shared/utils/queryClient';
import TradingPhotoCardResponse from '../../../components/api/model/TradingPhotoCardResponse';
import { useExploreTradingPhotoCards } from '@pocapan/shared/domains/tradingPhotoCards/ui/hooks/useExploreTradingPhotoCards';
import {
  useMatchedPhotoCards,
  useTradingPhotoCardsAll
} from '../../../components/api/queries/useTradingPhotoCards';
import TradingPhotoCardFilterBottomDrawer from './TradingPhotoCardFilterBottomDrawer';
import PhotoCardFilterButton from './PhotoCardFilterButton';
import ArtistDropdown from './ArtistDropdown';
import MemberDropdown from './MemberDropdown';
import { ListRenderer } from '../../../components/lists/ListRenderer';
import GridSkeletons from '../../../components/skeletons/GridSkeletons';
import EmptyExploreTradingPhotoCard from './EmptyExploreTradingPhotoCard';
import EmptyMatchedTradingPhotoCard from '../../../components/tradings/EmptyMatchedTradingPhotoCard';
import ExploreTradingPhotoCard from '../../../components/tradings/ExploreTradingPhotoCard';
import MatchedTargetTradingPhotoCard from '../../../components/tradings/MatchedTargetTradingPhotoCard';
import OnGoingMatchingTradingPhotoCard from '../../../components/tradings/OngoingMatchedTradingPhotoCard';
import TradingManagerBarV2 from './TradingManagerBarV2';

async function requestReMatching(memberId) {
  return fetch(`${baseUrl()}/trading_photo_cards/rematching`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`
    },
    body: JSON.stringify({
      member_id: memberId
    })
  }).then((data) => data.json());
}


export default function ExchangesTabPanel({
  fabWrapperRef,
  onShowBottomSheet,
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [selectedArtist, setSelectedArtist] = useState(null);
  const [onlyIdentityVerified, setOnlyIdentityVerified] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [selectedPhotoCardCategories, setSelectedPhotoCardCategories] = useState([]);

  const handleArtistSelected = (artist) => {
    setSelectedArtist(artist);
    setSelectedMembers([]);
    setOnlyIdentityVerified(false);
    setSelectedEventIds([]);
    setSelectedPhotoCardCategories([]);
    setPaginatedTradingPhotoCards(undefined);
  };
  const [selectedTradingPhotoCard, setSelectedTradingPhotoCard] = useState();
  const [paginatedTradingPhotoCards, setPaginatedTradingPhotoCards] = useState();
  const [isFilteringDrawerOpen, setIsFilteringDrawerOpen] = useState(false);

  const myTradingPhotoCards = useTradingPhotoCardsAll({ category: 'exchange' });
  const handlePaginatedTradingPhotoCards = (paginatedData) => {
    const tradingPhotoCards = paginatedData.pages?.flatMap((page) =>
      page.tradingPhotoCards.map(
        (tradingPhotoCard) => new TradingPhotoCardResponse(tradingPhotoCard)
      )
    );
    setPaginatedTradingPhotoCards(tradingPhotoCards);
  };

  const { data, isFetching, fetchNextPage, hasNextPage } = useExploreTradingPhotoCards({
    artistId: selectedArtist?.artistId,
    onlyIdentityVerified: onlyIdentityVerified,
    memberIds: selectedMembers.map((m) => m.memberId),
    photoCardCategories: selectedPhotoCardCategories,
    eventIds: selectedEventIds,
    category: 'exchange'
  });

  const loadMore = () => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    if (!isEmpty(data)) {
      handlePaginatedTradingPhotoCards(data);
    }
  }, [data]);

  const matchedTradingPhotoCards = useMatchedPhotoCards({
    artistId: selectedTradingPhotoCard?.artistId,
    tradingPhotoCardId: selectedTradingPhotoCard?.tradingPhotoCardId,
    memberId: selectedMembers[0]?.memberId,
    onlyIdentityVerified: onlyIdentityVerified,
    photoCardCategories: selectedPhotoCardCategories,
    eventIds: selectedEventIds
  });

  const isOngoingMatching = () => {
    if (isEmpty(selectedTradingPhotoCard)) return false;
    return (
      isNull(selectedTradingPhotoCard.matchedCount) || selectedTradingPhotoCard.matchedCount <= 0
    );
  };

  const onClickReMatch = async () => {
    if (isEmpty(selectedTradingPhotoCard)) return;
    if (!selectedTradingPhotoCard.memberId) return;

    const response = await requestReMatching(selectedTradingPhotoCard.memberId);
    if (response.success) {
      queryClient.invalidateQueries(['tradingPhotoCards']);
      setTimeout(() => {
        queryClient.invalidateQueries(['matchedTradingPhotoCards']);
      }, 1500);
    }
  };

  const onSelectedTradingPhotoCard = async (tradingPhotoCard) => {
    if (isEmpty(tradingPhotoCard)) {
      setSelectedTradingPhotoCard(null);
      initFilters();
    } else {
      const url = `/api/v1/artists/${tradingPhotoCard.artistId}`;
      const response = await queryGet(url);
      if (response?.artist) {
        setSelectedArtist(response.artist);
        initFilters();
        setSelectedTradingPhotoCard(tradingPhotoCard);
      }
    }
  };

  const initFilters = () => {
    setOnlyIdentityVerified(false);
    setSelectedEventIds([]);
    setSelectedPhotoCardCategories([]);
  };

  const handleFilterSelected = (filteredObj) => {
    setIsFilteringDrawerOpen(false);
    if (!isEmpty(filteredObj)) {
      setOnlyIdentityVerified(filteredObj.identityVerified);
      setSelectedEventIds(filteredObj.eventIds);
      setSelectedPhotoCardCategories(filteredObj.photoCardCategories);

      queryClient.invalidateQueries(['matchedTradingPhotoCards']);
    }
  };

  const onOpenFilterDrawer = (isOpen) => {
    if (fabWrapperRef.current) {
      fabWrapperRef.current.style.visibility = isOpen ? 'hidden' : 'visible';
    }
    setIsFilteringDrawerOpen(isOpen);
  };

  const onOpenedCardBottomSheet = (isOpen) => {
    if (onShowBottomSheet) {
      onShowBottomSheet(isOpen);
    }
  };

  useEffect(() => {
    if (onShowBottomSheet) {
      onShowBottomSheet(isFilteringDrawerOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilteringDrawerOpen]);

  return (
    <>
      <div className="pb-6 border-b border-surface-light dark:border-surface-dark mt-[16px]">
        <TradingManagerBarV2
          tradingPhotoCards={myTradingPhotoCards}
          isShowExploreCard={true}
          category={'exchange'}
          isShowChatRoomsCount={true}
          onClickTradingPhotoCard={(tradingPhotoCard) =>
            onSelectedTradingPhotoCard(tradingPhotoCard)
          }
        />
      </div>
      {!isEmpty(selectedTradingPhotoCard) && selectedTradingPhotoCard.chatRoomsCount > 0 && (
        <button
          type="button"
          className="py-[5px] w-full bg-surface-light dark:bg-surface-dark" // FIXME : background: #0000000d;
          onClick={() =>
            navigate(
              `/trading_photo_cards/${selectedTradingPhotoCard.tradingPhotoCardId}/my_chat_rooms`
            )
          }
        >
          <div className="flex justify-center items-center">
            <div className="py-[11px] text-[14px] font-semibold leading-[17px] text-center bg-gradient-to-b from-[#1e4bc7] to-[#8400f0] bg-clip-text text-transparent">
              {t('ExchangesTabPanel.chatRoomsBoxText', {
                count: selectedTradingPhotoCard.chatRoomsCount
              })}
            </div>
            <ArrowRight size={15} className="text-[#1E4AC7]" />
          </div>
        </button>
      )}

      <div className="pt-[24px] pb-[16px] px-[24px]">
        <div className="flex items-center gap-[10px]">
          <ArtistDropdown onSelected={handleArtistSelected} />
          {selectedArtist && (
            <MemberDropdown
              members={selectedArtist.members}
              selectedMembers={selectedMembers}
              onSelectedMembers={setSelectedMembers}
            />
          )}
          {isEmpty(selectedTradingPhotoCard) && (
            <PhotoCardFilterButton
              selectedPhotoCardCategories={selectedPhotoCardCategories}
              selectedEventIds={selectedEventIds}
              onlyIdentityVerified={onlyIdentityVerified}
              onClick={() => onOpenFilterDrawer(true)}
            />
          )}
        </div>
      </div>
      {isEmpty(selectedTradingPhotoCard) ? (
        <div className="px-6">
          <ListRenderer
            data={paginatedTradingPhotoCards}
            skeletonComponent={
              <GridSkeletons
                gridSize={3}
                skeletonSize={3}
                skeletonHeight={'96px'}
                showText={true}
              />
            }
            emptyComponent={<EmptyExploreTradingPhotoCard />}
            renderList={(data) => (
              <PaginatedTradingPhotoCardsList
                paginatedTradingPhotoCards={data}
                loadMore={loadMore}
                hasNextPage={hasNextPage}
                onShowBottomSheet={onOpenedCardBottomSheet}
              />
            )}
          />
        </div>
      ) : isEmpty(matchedTradingPhotoCards) ? (
        <RenderOngoingOrEmptyMatch
          isOngoingMatching={isOngoingMatching()}
          onClickReMatch={onClickReMatch}
        />
      ) : (
        <div className="px-6">
          <RenderMatchedTradingPhotoCardsList matchedTradingPhotoCards={matchedTradingPhotoCards} />
        </div>
      )}
      {isFilteringDrawerOpen && (
        <TradingPhotoCardFilterBottomDrawer
          isOpen={isFilteringDrawerOpen}
          onClose={() => onOpenFilterDrawer(false)}
          onConfirm={(filteredObj) => handleFilterSelected(filteredObj)}
          identityVerified={onlyIdentityVerified}
          artistId={selectedArtist?.artistId}
          eventIds={selectedEventIds}
          photoCardCategories={selectedPhotoCardCategories}
        />
      )}
    </>
  );
}

const RenderOngoingOrEmptyMatch = ({ isOngoingMatching, onClickReMatch }) => (
  <div className="px-6">
    {isOngoingMatching ? (
      <OnGoingMatchingTradingPhotoCard />
    ) : (
      <EmptyMatchedTradingPhotoCard onClickReMatch={onClickReMatch} />
    )}
  </div>
);


const RenderMatchedTradingPhotoCardsList = ({ matchedTradingPhotoCards }) => (
  <div className="pb-[calc(56px+env(safe-area-inset-bottom))]">
    <Virtuoso
      useWindowScroll
      overscan={480}
      data={chunk(matchedTradingPhotoCards, 3)}
      style={{ height: '100px' }}
      itemContent={(index, chunkCards) => (
        <div key={index} className="grid grid-cols-3 gap-[7px] mb-6">
          {chunkCards.map((card) => {
            return (
              <MatchedTargetTradingPhotoCard
                key={card.matchedTradingPhotoCardId}
                matchedTradingPhotoCard={card}
              />
            );
          })}
        </div>
      )}
    />
  </div>
);

const PaginatedTradingPhotoCardsList = ({
  paginatedTradingPhotoCards,
  hasNextPage,
  loadMore,
  onShowBottomSheet
}) => (
  <div className="pb-[calc(56px+env(safe-area-inset-bottom))]">
    <EasyVirtualized
      useParentScrollElement={true}
      overscanRowCount={3}
      onLoadMore={loadMore}
      hasMore={hasNextPage}
      loader={
        <div className="flex justify-center mt-2">
          <Spinner size="sm" className="text-primary-light dark:text-primary-dark mr-5" />
        </div>
      }
    >
      {chunk(paginatedTradingPhotoCards, 3).map((chunkCards) => (
        <div key={chunkCards[0].tradingPhotoCardId} className="grid grid-cols-3 gap-[7px] mb-6">
          {chunkCards.map((card) => {
            return (
              <ExploreTradingPhotoCard
                key={card.tradingPhotoCardId}
                tradingPhotoCard={card}
                onShowBottomSheet={onShowBottomSheet}
              />
            );
          })}
        </div>
      ))}
    </EasyVirtualized>
  </div>
);
